import { z } from "zod";
import { listQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { timestampsShape } from "./common.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";

/**
 * INTEGRATIONS (PRD §9 Item 16) — the contract for the integrations hub.
 *
 * Two layers:
 *
 *  · THE FOUNDATION every provider shares: a connection (credentials held
 *    encrypted, NEVER sent back), the webhook inbox (one row per provider event,
 *    deduplicated), the outbound delivery log (one row per call we make) and the
 *    health a connection derives from them. Payment gateways and the
 *    communication channels (WhatsApp, Slack, Zapier, Drive) all sit on it.
 *
 *  · PAYMENT GATEWAYS (Razorpay first, Stripe for foreign clients): a "Pay now"
 *    link per invoice and the payments the gateway reports back. The legal GST
 *    invoice is always ours — a gateway only collects the money
 *    (integrations-reference §0 decision 1).
 *
 * Secrets travel IN on the connect inputs and never OUT: a connection DTO carries
 * a `credentialsHint` ("rzp_live_…a1B2") and nothing else about them.
 */

// ---------------------------------------------------------------------------
// Connections (the foundation)
// ---------------------------------------------------------------------------

export const INTEGRATION_CONNECTION_STATUSES = ["connected", "error", "disconnected"] as const;
export type IntegrationConnectionStatus = (typeof INTEGRATION_CONNECTION_STATUSES)[number];
export const integrationConnectionStatusSchema = z.enum(INTEGRATION_CONNECTION_STATUSES);

/**
 * Derived for the hub (PRD US-16.1): `pending` = connected but never verified,
 * `healthy` = last call succeeded, `degraded` = connected but the latest thing
 * that happened was a failure, `error` = the provider refused our credentials.
 */
export const INTEGRATION_HEALTH = ["pending", "healthy", "degraded", "error", "disconnected"] as const;
export type IntegrationHealth = (typeof INTEGRATION_HEALTH)[number];
export const integrationHealthSchema = z.enum(INTEGRATION_HEALTH);

export const integrationModeSchema = z.enum(["live", "test"]);
export type IntegrationMode = z.infer<typeof integrationModeSchema>;

/** GET /api/v1/integrations/connections/:id (perm `integrations:manage`). Credentials are never included. */
export const integrationConnectionSchema = z.object({
  id: uuidSchema,
  /** `razorpay`, `stripe`, `whatsapp`, `slack`, … — open-ended, each provider owns its key. */
  provider: z.string(),
  /** Distinguishes two accounts of one provider (a Slack team id); "" when there is only ever one. */
  accountRef: z.string(),
  displayName: z.string().nullable(),
  status: integrationConnectionStatusSchema,
  health: integrationHealthSchema,
  mode: integrationModeSchema.nullable(),
  /** A recognisable, non-secret fragment of the credential ("rzp_live_…a1B2"). */
  credentialsHint: z.string().nullable(),
  /** Non-secret, provider-specific settings (link expiry, success URL…). */
  config: z.record(z.string(), z.unknown()),
  /** Where the provider must POST its webhooks, when it sends any. */
  webhookUrl: z.string().nullable(),
  connectedByUserId: uuidSchema.nullable(),
  connectedAt: timestampSchema.nullable(),
  lastOkAt: timestampSchema.nullable(),
  lastError: z.string().nullable(),
  lastErrorAt: timestampSchema.nullable(),
  lastWebhookAt: timestampSchema.nullable(),
  lastSyncedAt: timestampSchema.nullable(),
  disconnectedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type IntegrationConnectionDto = z.infer<typeof integrationConnectionSchema>;

/** GET /api/v1/integrations/connections (perm `integrations:manage`). */
export const listConnectionsQuerySchema = z.object({
  provider: z.string().trim().max(40).optional(),
  status: integrationConnectionStatusSchema.optional(),
});
export type ListConnectionsQuery = z.infer<typeof listConnectionsQuerySchema>;

/** DELETE /api/v1/integrations/connections/:id (perm `integrations:manage`) → 204. */
export const disconnectConnectionQuerySchema = z.object({
  /** US-16.1: keep (default) or remove the id mappings this connection synced. */
  removeReferences: z.enum(["true", "false"]).optional(),
});
export type DisconnectConnectionQuery = z.infer<typeof disconnectConnectionQuerySchema>;

/** POST /api/v1/integrations/connections/:id/sync (perm `integrations:manage`) → 202. */
export const connectionSyncResultSchema = z.object({
  connectionId: uuidSchema,
  queued: z.boolean(),
  jobId: uuidSchema.nullable(),
});
export type ConnectionSyncResultDto = z.infer<typeof connectionSyncResultSchema>;

// ---------------------------------------------------------------------------
// The webhook inbox and the outbound delivery log
// ---------------------------------------------------------------------------

export const INBOUND_EVENT_STATUSES = ["received", "processed", "ignored", "failed"] as const;
export type InboundEventStatus = (typeof INBOUND_EVENT_STATUSES)[number];
export const inboundEventStatusSchema = z.enum(INBOUND_EVENT_STATUSES);

/** One webhook delivery as the provider sent it (principles §8.2). */
export const inboundEventSchema = z.object({
  id: uuidSchema,
  provider: z.string(),
  connectionId: uuidSchema.nullable(),
  /** The provider's own event id — the dedupe key. */
  eventId: z.string(),
  eventType: z.string().nullable(),
  signatureValid: z.boolean(),
  status: inboundEventStatusSchema,
  attempts: z.number().int(),
  replayCount: z.number().int(),
  receivedAt: timestampSchema,
  processedAt: timestampSchema.nullable(),
  lastError: z.string().nullable(),
  /** What processing did ("recorded payment …", "ignored: already captured"). */
  result: z.record(z.string(), z.unknown()).nullable(),
  sizeBytes: z.number().int(),
});
export type InboundEventDto = z.infer<typeof inboundEventSchema>;

/** GET /api/v1/integrations/inbound-events/:id (perm `integrations:manage`) — with the payload itself. */
export const inboundEventDetailSchema = inboundEventSchema.extend({
  payload: z.unknown(),
});
export type InboundEventDetailDto = z.infer<typeof inboundEventDetailSchema>;

/** GET /api/v1/integrations/inbound-events (perm `integrations:manage`) → page, newest first. */
export const listInboundEventsQuerySchema = listQuerySchema.extend({
  provider: z.string().trim().max(40).optional(),
  status: inboundEventStatusSchema.optional(),
});
export type ListInboundEventsQuery = z.infer<typeof listInboundEventsQuerySchema>;

export const OUTBOUND_DELIVERY_STATUSES = ["pending", "attempting", "succeeded", "failed", "ambiguous"] as const;
export type OutboundDeliveryStatus = (typeof OUTBOUND_DELIVERY_STATUSES)[number];
export const outboundDeliveryStatusSchema = z.enum(OUTBOUND_DELIVERY_STATUSES);

/** One call we made to a provider — what the health page and "why did this not go out?" read. */
export const outboundDeliverySchema = z.object({
  id: uuidSchema,
  provider: z.string(),
  connectionId: uuidSchema.nullable(),
  operation: z.string(),
  idempotencyKey: z.string(),
  entityType: z.string().nullable(),
  entityId: uuidSchema.nullable(),
  status: outboundDeliveryStatusSchema,
  attempts: z.number().int(),
  httpStatus: z.number().int().nullable(),
  latencyMs: z.number().int().nullable(),
  externalId: z.string().nullable(),
  /** True when an ambiguous failure was settled by asking the provider rather than by calling again. */
  reconciled: z.boolean(),
  lastError: z.string().nullable(),
  nextRetryAt: timestampSchema.nullable(),
  requestSummary: z.record(z.string(), z.unknown()),
  responseSummary: z.record(z.string(), z.unknown()).nullable(),
  createdAt: timestampSchema,
  completedAt: timestampSchema.nullable(),
});
export type OutboundDeliveryDto = z.infer<typeof outboundDeliverySchema>;

/** GET /api/v1/integrations/outbound-deliveries (perm `integrations:manage`) → page, newest first. */
export const listOutboundDeliveriesQuerySchema = listQuerySchema.extend({
  provider: z.string().trim().max(40).optional(),
  status: outboundDeliveryStatusSchema.optional(),
});
export type ListOutboundDeliveriesQuery = z.infer<typeof listOutboundDeliveriesQuerySchema>;

// ---------------------------------------------------------------------------
// Payment gateways
// ---------------------------------------------------------------------------

export const PAYMENT_PROVIDERS = ["razorpay", "stripe"] as const;
export type PaymentProviderKey = (typeof PAYMENT_PROVIDERS)[number];
export const paymentProviderSchema = z.enum(PAYMENT_PROVIDERS);

export const PAYMENT_PROVIDER_LABELS: Record<PaymentProviderKey, string> = {
  razorpay: "Razorpay",
  stripe: "Stripe",
};

/** Razorpay settings — every key optional; omitted keys keep their current value. */
const razorpayConfigShape = {
  /** Let the client pay part of the balance through the link (off: the link asks for the whole balance). */
  acceptPartial: z.boolean(),
  /** Days a link stays payable (Razorpay allows up to about six months). */
  linkExpiryDays: z.number().int().min(1).max(180),
  /** Raise a link automatically for every invoice issued while connected (PRD US-16.2 "on every invoice"). */
  autoCreateOnIssue: z.boolean(),
  /** Where Razorpay sends the payer after paying. The webhook — never this redirect — records the money. */
  callbackUrl: z.string().trim().url().max(500).nullable(),
};

/**
 * PUT /api/v1/integrations/payments/razorpay (perm `integrations:manage`) → Connection.
 *
 * The agency's OWN keys: money settles to the agency's Razorpay account, never
 * to us. `keySecret` and `webhookSecret` are required on the first connect and
 * optional afterwards (omitted = keep; sent = rotate). Stored encrypted; never
 * returned. A new `webhookSecret` keeps the previous one valid for 24 h so no
 * delivery in flight is refused during a rotation.
 */
export const connectRazorpayInputSchema = z
  .object({
    keyId: z
      .string()
      .trim()
      .regex(/^rzp_(live|test)_[A-Za-z0-9]{6,40}$/, "Use the Key Id from Razorpay Dashboard → Settings → API Keys (rzp_live_… or rzp_test_…)"),
    keySecret: z.string().trim().min(8).max(200).optional(),
    webhookSecret: z.string().min(8).max(200).optional(),
    displayName: z.string().trim().min(1).max(80).optional(),
    config: z.object(razorpayConfigShape).partial().strict().optional(),
    /**
     * The caller's own password, re-entered.
     *
     * Whoever controls the gateway keys controls where the agency's clients'
     * money lands, which is the same class of risk as the bank details on an
     * invoice and is guarded the same way.
     */
    password: z.string().min(1).max(200),
    totpCode: z.string().min(6).max(10).optional(),
  })
  .strict();
export type ConnectRazorpayInput = z.input<typeof connectRazorpayInputSchema>;

/** Stripe settings — every key optional; omitted keys keep their current value. */
const stripeConfigShape = {
  autoCreateOnIssue: z.boolean(),
  /** Where Checkout sends the payer after paying / abandoning. Defaults to the app. */
  successUrl: z.string().trim().url().max(500).nullable(),
  cancelUrl: z.string().trim().url().max(500).nullable(),
};

/**
 * PUT /api/v1/integrations/payments/stripe (perm `integrations:manage`) → Connection.
 * Same rules as Razorpay: the agency's own secret key, required on first connect,
 * encrypted at rest, never returned; a rotated `webhookSecret` overlaps for 24 h.
 */
export const connectStripeInputSchema = z
  .object({
    secretKey: z
      .string()
      .trim()
      .regex(/^(sk|rk)_(live|test)_[A-Za-z0-9]{10,250}$/, "Use a secret (sk_…) or restricted (rk_…) key from the Stripe dashboard")
      .optional(),
    webhookSecret: z
      .string()
      .trim()
      .regex(/^whsec_[A-Za-z0-9+/=_-]{10,250}$/, "Use the endpoint's signing secret (whsec_…)")
      .optional(),
    displayName: z.string().trim().min(1).max(80).optional(),
    config: z.object(stripeConfigShape).partial().strict().optional(),
    /** The caller's own password, re-entered. See the Razorpay schema. */
    password: z.string().min(1).max(200),
    totpCode: z.string().min(6).max(10).optional(),
  })
  .strict();
export type ConnectStripeInput = z.input<typeof connectStripeInputSchema>;

export const PAY_LINK_STATUSES = ["requested", "active", "partially_paid", "paid", "expired", "cancelled", "failed"] as const;
export type PayLinkStatus = (typeof PAY_LINK_STATUSES)[number];
export const payLinkStatusSchema = z.enum(PAY_LINK_STATUSES);

/**
 * One gateway link for one invoice. `requested` = queued, the URL arrives when
 * the job has talked to the provider. Money is masked (null) for callers
 * without `financials:view`.
 */
export const payLinkSchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  provider: paymentProviderSchema,
  status: payLinkStatusSchema,
  /** 1 for the first link; a re-issue after the balance changed is 2, 3… */
  attempt: z.number().int(),
  referenceId: z.string(),
  amountMinor: minorSchema.nullable(),
  amountPaidMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  url: z.string().nullable(),
  externalId: z.string().nullable(),
  expiresAt: timestampSchema.nullable(),
  lastError: z.string().nullable(),
  ...timestampsShape,
});
export type PayLinkDto = z.infer<typeof payLinkSchema>;

/**
 * GET /api/v1/integrations/invoices/:id/pay-link (perm `invoices:read`) — the
 * invoice's live "Pay now" link (null when none), and the gateways it could use.
 * The same shape answers the portal route.
 */
export const invoicePayLinkSchema = z.object({
  invoiceId: uuidSchema,
  payLink: payLinkSchema.nullable(),
  availableProviders: z.array(paymentProviderSchema),
});
export type InvoicePayLinkDto = z.infer<typeof invoicePayLinkSchema>;

/**
 * POST /api/v1/integrations/invoices/:id/pay-link (perm `invoices:send`) → 201 when
 * a link was requested, 200 when the live one was returned as is.
 * No connected gateway → CONFLICT; nothing left to pay → CONFLICT.
 */
export const requestPayLinkInputSchema = z
  .object({
    /** Default: Razorpay for INR (or when it is the only one), Stripe for other currencies. */
    provider: paymentProviderSchema.optional(),
    /** Cancel the live link and issue a fresh one even if it is still valid. */
    reissue: z.boolean().optional(),
  })
  .strict();
export type RequestPayLinkInput = z.input<typeof requestPayLinkInputSchema>;

export const GATEWAY_PAYMENT_STATUSES = ["failed", "authorized", "captured", "refunded"] as const;
export type GatewayPaymentStatus = (typeof GATEWAY_PAYMENT_STATUSES)[number];
export const gatewayPaymentStatusSchema = z.enum(GATEWAY_PAYMENT_STATUSES);

/** What a gateway told us about one payment attempt. Money masked without `financials:view`. */
export const gatewayPaymentSchema = z.object({
  id: uuidSchema,
  provider: paymentProviderSchema,
  providerPaymentId: z.string(),
  invoiceId: uuidSchema.nullable(),
  /** The receipt it became in the payments ledger (null until captured and matched). */
  paymentId: uuidSchema.nullable(),
  status: gatewayPaymentStatusSchema,
  amountMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  feeMinor: minorSchema.nullable(),
  taxMinor: minorSchema.nullable(),
  refundedMinor: minorSchema.nullable(),
  method: z.string().nullable(),
  errorCode: z.string().nullable(),
  errorDescription: z.string().nullable(),
  capturedAt: timestampSchema.nullable(),
  failedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type GatewayPaymentDto = z.infer<typeof gatewayPaymentSchema>;

/**
 * GET /api/public/v1/pay/:token — the stable "Pay now" address for an email or a
 * public invoice page. The token is the invoice's signed view link. `ready`
 * carries the gateway URL; `preparing` means a link was just requested (ask
 * again in `retryAfterSeconds`); `paid` / `unavailable` need no action.
 * `?redirect=true` answers 302 to the gateway when ready.
 */
export const publicPayStatusSchema = z.object({
  status: z.enum(["ready", "preparing", "paid", "unavailable"]),
  url: z.string().nullable(),
  provider: paymentProviderSchema.nullable(),
  invoiceNumber: z.string().nullable(),
  amountMinor: minorSchema.nullable(),
  currency: currencyCodeSchema.nullable(),
  retryAfterSeconds: z.number().int().nullable(),
});
export type PublicPayStatusDto = z.infer<typeof publicPayStatusSchema>;

/** Every provider webhook answers this, fast (principles §8.2 — processing is asynchronous). */
export const webhookAckSchema = z.object({
  received: z.boolean(),
  duplicate: z.boolean(),
});
export type WebhookAckDto = z.infer<typeof webhookAckSchema>;
